// -----------------------------------------------------------------------------
// Renders a chat message body, turning inline @-mention tokens into chips.
// Navigable entities (vehicle / stock / order / lead) become in-app links;
// user mentions render as a non-navigating highlight (they're people in the
// conversation, not a record). Styling adapts to the bubble it sits in — chips
// on a primary-filled "own" bubble use a translucent on-primary treatment.
// -----------------------------------------------------------------------------

import { Link } from "react-router-dom";

import classNames from "../../utils/classNames";
import {
  Mention,
  mentionDetailUrl,
  parseMentions,
} from "../../utils/chatMentions";

interface ChatMessageBodyProps {
  body: string;
  /** True when shown inside the current user's own (primary-filled) bubble. */
  onPrimary: boolean;
}

export default function ChatMessageBody({ body, onPrimary }: ChatMessageBodyProps) {
  const segments = parseMentions(body);

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.kind === "text") {
          return <span key={index}>{segment.text}</span>;
        }
        return (
          <MentionChip
            key={index}
            mention={segment.mention}
            onPrimary={onPrimary}
          />
        );
      })}
    </>
  );
}

function MentionChip({
  mention,
  onPrimary,
}: {
  mention: Mention;
  onPrimary: boolean;
}) {
  const url = mentionDetailUrl(mention.type, mention.id);

  const className = classNames(
    "mx-0.5 inline-flex items-center rounded px-1 py-px text-[0.95em] font-medium align-baseline",
    onPrimary
      ? "bg-white/20 text-white"
      : mention.type === "user"
        ? "bg-primary-50 text-primary-700"
        : "bg-primary-50 text-primary-700 hover:bg-primary-100"
  );

  const label = `@${mention.label}`;

  if (!url) {
    return <span className={className}>{label}</span>;
  }

  return (
    <Link to={url} className={className}>
      {label}
    </Link>
  );
}
